import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ScreenLoader } from '@/components/common/screen-loader';
import { useAuth } from '@tagna/auth';
import { Loader2 } from 'lucide-react';

const ADMIN_ROLE_SET = new Set(['admin', 'prime', 'identity.admin']);
const REDIRECT_PATH_KEY = 'redirectPath';
const LOGIN_REDIRECT_LOCK_KEY = 'admin_login_redirect_lock';
const LOGIN_REDIRECT_LOCK_TTL_MS = 15_000;

const hasAdminRole = (roles: string[]) => {
  return roles.some((role) => ADMIN_ROLE_SET.has(role.trim().toLowerCase()));
};

const isSafeRedirectPath = (path: string): boolean => {
  return path.startsWith('/') && !path.startsWith('/auth/');
};

/**
 * Component to protect routes that require authentication.
 * If user is not authenticated, redirects to the login page.
 */
export const RequireAuth = () => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();

  // Show screen loader while checking authentication
  if (isLoading) {
    return <ScreenLoader />;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <AuthRedirect />;
  }

  // If authenticated but not an admin, block access
  if (!user || !hasAdminRole(user.roles || [])) {
    return <AccessDenied onSignOut={logout} roles={user?.roles || []} />;
  }

  // If authenticated, render child routes
  return <Outlet />;
};

const AuthRedirect = () => {
  const { login } = useAuth();
  const location = useLocation();
  const startedRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }

    startedRef.current = true;
    const redirectPath = `${location.pathname}${location.search}${location.hash}`;

    if (redirectPath && isSafeRedirectPath(redirectPath)) {
      sessionStorage.setItem(REDIRECT_PATH_KEY, redirectPath);
    } else {
      sessionStorage.setItem(REDIRECT_PATH_KEY, '/');
    }

    const now = Date.now();
    const lockValue = Number(sessionStorage.getItem(LOGIN_REDIRECT_LOCK_KEY) || '0');
    if (Number.isFinite(lockValue) && now - lockValue < LOGIN_REDIRECT_LOCK_TTL_MS) {
      return;
    }
    sessionStorage.setItem(LOGIN_REDIRECT_LOCK_KEY, String(now));

    login().catch((error) => {
      console.error('Failed to start login redirect:', error);
      sessionStorage.removeItem(LOGIN_REDIRECT_LOCK_KEY);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Unable to contact the identity service. Please check your connection and try again.',
      );
    });
  }, [login, location.pathname, location.search, location.hash]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="max-w-sm w-full text-center space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Signing you in.
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Redirecting to Tagna Identity to continue.
          </p>
        </div>

        {!errorMessage && (
          <div className="flex items-center justify-center">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
          </div>
        )}

        {errorMessage && (
          <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-md text-sm">
            {errorMessage}
          </div>
        )}

        <p className="text-xs text-gray-400">
          If nothing happens, ensure https://localhost:44311 is trusted in your
          browser, then refresh this page.
        </p>
      </div>
    </div>
  );
};

const AccessDenied = ({
  onSignOut,
  roles,
}: {
  onSignOut: () => Promise<void>;
  roles: string[];
}) => {
  const roleLabel = roles.length > 0 ? roles.join(', ') : 'None';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 text-center">
      <div className="max-w-md space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Access denied
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            This application is restricted to admin roles only.
          </p>
          <p className="mt-2 text-xs text-gray-500">Your roles: {roleLabel}</p>
        </div>
        <button
          type="button"
          onClick={() => void onSignOut()}
          className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-primary text-white font-medium hover:bg-primary/90"
        >
          Sign out
        </button>
      </div>
    </div>
  );
};
